/**
 * Zutaten eines Rezepts auf die Einkaufsliste übernehmen.
 *
 * Die Mengen gelten für die gewählte Portionszahl. Vorräte werden abgezogen,
 * Selbstverständliches (Leitungswasser, Gewürze ohne Menge) ist abgewählt.
 * Auf der Liste fasst der Server gleiche Zutaten zusammen.
 */
import { recipeNeeds, shoppingKey, subtractPantry, usuallyAtHome } from '@cookbook/domain';
import type { Measure, ShoppingNeed } from '@cookbook/domain';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../../api/client.ts';
import type { PantryItem, RecipeDetail, ShoppingList } from '../../api/types.ts';
import { useCachedQuery } from '../../api/useCachedQuery.ts';
import { itemAmount } from '../../lib/shoppingFormat.ts';
import { texts } from '../../texts/de.ts';

/** Eigenschaften des Dialogs. */
export interface AddToShoppingDialogProps {
  readonly recipe: RecipeDetail;
  readonly factor: number;
  readonly onClose: () => void;
}

/** Ein Vorschlag im Dialog. */
interface Proposal {
  readonly need: ShoppingNeed;
  /** Was nach Abzug des Vorrats bleibt; null heißt: Vorrat reicht. */
  readonly remaining: Measure | null;
  readonly fromPantry: boolean;
  readonly atHome: boolean;
}

/** Vergleicht Bedarf und Vorrat. */
function propose(needs: readonly ShoppingNeed[], pantry: readonly PantryItem[]): Proposal[] {
  return needs.map((need) => {
    const atHome = usuallyAtHome(need.name);
    const key = shoppingKey(need.name);
    for (const stock of pantry.filter((entry) => entry.key === key)) {
      const result = subtractPantry(need.measure, { amount: stock.amount, unit: stock.unit }, need.name);
      if (result.used) {
        return { need, remaining: result.remaining, fromPantry: true, atHome };
      }
    }
    return { need, remaining: need.measure, fromPantry: false, atHome };
  });
}

/** Vorauswahl: Was gekauft werden muss. Gewürze ohne Menge sind meist da. */
function preselected(proposal: Proposal): boolean {
  if (proposal.atHome || proposal.remaining === null) {
    return false;
  }
  return !(proposal.need.category === 'spices' && proposal.need.measure.amount === null);
}

/** Dialog zum Übernehmen der Zutaten. */
export function AddToShoppingDialog({ recipe, factor, onClose }: AddToShoppingDialogProps) {
  const lists = useCachedQuery<{ lists: ShoppingList[] }>('/api/shopping/lists', 'einkauf.listen');
  const pantry = useCachedQuery<{ items: PantryItem[] }>('/api/pantry', 'einkauf.vorrat');
  const proposals = useMemo(
    () => propose(recipeNeeds(recipe.ingredients, factor), pantry.data?.items ?? []),
    [recipe.ingredients, factor, pantry.data],
  );
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [listId, setListId] = useState<string>('');
  const [result, setResult] = useState<{ added: number; merged: number } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Vorauswahl, sobald der Vorrat bekannt ist (oder feststeht, dass er fehlt).
  const ready = !pantry.loading || pantry.data !== null;
  const chosen =
    selected ?? new Set(ready ? proposals.filter(preselected).map((proposal) => proposal.need.key) : []);
  const targetList = listId === '' ? (lists.data?.lists[0]?.id ?? '') : listId;

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  /** Schaltet die Auswahl einer Zutat um. */
  function toggle(key: string) {
    const next = new Set(chosen);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelected(next);
  }

  /** Überträgt die gewählten Zutaten. */
  async function submit() {
    setBusy(true);
    setMessage(null);
    try {
      const items = proposals
        .filter((proposal) => chosen.has(proposal.need.key))
        .map((proposal) => ({
          name: proposal.need.name,
          amount: proposal.remaining?.amount ?? proposal.need.measure.amount,
          unit: proposal.remaining?.unit ?? proposal.need.measure.unit,
          note: proposal.need.note === '' ? null : proposal.need.note,
          category: proposal.need.category,
          productId: proposal.need.productId,
          source: { recipeId: recipe.id, title: recipe.title },
        }));
      const response = await api.post<{ added: number; merged: number }>(
        `/api/shopping/lists/${targetList}/items`,
        { items },
      );
      setResult({ added: response.added, merged: response.merged });
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-to-shopping-title"
        onClick={(event) => event.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-surface-raised p-5 shadow-xl sm:rounded-3xl"
      >
        <h2 id="add-to-shopping-title" className="text-lg font-bold">
          {texts.shopping.addDialogTitle}
        </h2>
        <p className="mb-3 text-sm text-content-muted">{texts.shopping.addDialogHint}</p>

        {result !== null ? (
          <div className="space-y-3">
            <p className="rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
              {texts.shopping.added}: {result.added} {texts.shopping.addedSummary} {result.merged}
            </p>
            <div className="flex gap-2">
              <Link to="/einkauf" className="rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent">
                {texts.shopping.openList}
              </Link>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-border-subtle px-4 py-2"
              >
                {texts.common.close}
              </button>
            </div>
          </div>
        ) : (
          <>
            <ul className="mb-4 divide-y divide-border-subtle">
              {proposals.map((proposal) => {
                const shown = proposal.remaining ?? proposal.need.measure;
                const amount =
                  shown.amount === null ? '' : itemAmount({ amount: shown.amount, unit: shown.unit });
                const hint = proposal.atHome
                  ? texts.shopping.atHome
                  : proposal.fromPantry
                    ? proposal.remaining === null
                      ? texts.shopping.fromPantry
                      : `${texts.shopping.pantryDeducted} ${itemAmount(proposal.need.measure)}`
                    : null;
                return (
                  <li key={proposal.need.key}>
                    <label className="flex cursor-pointer items-center gap-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={chosen.has(proposal.need.key)}
                        onChange={() => toggle(proposal.need.key)}
                        className="h-5 w-5 accent-[var(--color-accent)]"
                      />
                      <span className="flex-1">
                        {amount === '' ? null : <strong className="mr-1 tabular-nums">{amount}</strong>}
                        {proposal.need.name}
                        {hint === null ? null : (
                          <span className="block text-xs text-content-muted">{hint}</span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <span className="text-content-muted">{texts.shopping.targetList}</span>
                <select
                  value={targetList}
                  onChange={(event) => setListId(event.target.value)}
                  className="rounded-xl border border-border-subtle bg-surface px-2 py-1.5"
                >
                  {(lists.data?.lists ?? []).map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={busy || chosen.size === 0 || targetList === ''}
                className="ml-auto rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent disabled:opacity-50"
              >
                {chosen.size} {texts.shopping.addSelected}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-border-subtle px-4 py-2"
              >
                {texts.common.cancel}
              </button>
            </div>
            {message === null ? null : (
              <p className="mt-3 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{message}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
